import React, { useState } from 'react';
import { ENUM_ANCHORS, gameModeOptions } from '../../../../enums/enums';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ShowContainer from '../../../../components/material-ui/ShowContainer';
import SelectGame from '../../../../components/material-ui/SelectGame';
import useGroupByGroupId from '../../../../hooks/site/useGroupByGroupId';
import CusLabelSelect from '../../../../components/material-ui/CusLabelSelect';
import useBootstrapSize from '../../../../hooks/useBootstrapSize';

export default function AnchorsLink({
  title = "You can add anchors to the site's referral link that control the opening of modal windows",
  exampleLink,
  onAnchorClick,
  openGameClick,
  isOpenGame
}) {
  const { t } = useTranslation();
  const { isMobile } = useBootstrapSize();
  const { group } = useGroupByGroupId();
  const [selectMode, setSelectMode] = useState();
  const [selectGame, setSelectGame] = useState();

  const groupId = group.parentId;

  const anchors = [
    { name: ENUM_ANCHORS.signup, description: 'anchor opens the registration window' },
    {
      name: ENUM_ANCHORS.signupOn,
      description: 'anchor opens registration window without possibility to close'
    },
    {
      name: ENUM_ANCHORS.signupDepositOn,
      description:
        'the anchor opens a registration window without the ability to close it, and after registration a modal deposit window without the ability to close it'
    },
    {
      name: ENUM_ANCHORS.fortune,
      description: "anchor opens the 'Wheel of Fortune' window and the registration modal window"
    },
    {
      name: ENUM_ANCHORS.fortuneOn,
      description:
        "anchor opens the 'Wheel of Fortune' window and the registration modal window without the ability to close"
    },
    {
      name: ENUM_ANCHORS.fortuneOn,
      description:
        "anchor opens the 'Wheel of Fortune' window and the registration modal window without the ability to close"
    },
    {
      name: `${ENUM_ANCHORS.deposit}?currency=RUB&amount=100`,
      description: 'The anchor will open a balance replenishment window.'
    },
    {
      name: ENUM_ANCHORS.promocode,
      description: 'Clicking the link will open the promo code activation window.'
    }
  ];

  return (
    <List>
      <Typography variant="h6" mb={1} mt={2} color="textPrimary">
        {t(title)}
      </Typography>

      <ShowContainer condition={isOpenGame}>
        <ListItem style={{ padding: 1 }}>
          <ListItemText
            primary={
              <Stack
                gap={1}
                direction={isMobile ? 'column' : 'row'}
                alignItems={'center'}
                sx={{ paddingBottom: isMobile ? 0.5 : 0 }}>
                <ShowContainer condition={!isMobile}>
                  <Typography color="textSecondary" fontWeight={600}>
                    {t('Launching the game')}
                  </Typography>
                </ShowContainer>

                <Tooltip title={t('Start writing')}>
                  <Box
                    flex={1}
                    width={isMobile ? '100%' : ''}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}>
                    <SelectGame
                      placeholder={'Game title'}
                      groupId={groupId}
                      value={selectGame}
                      onChange={(_, info) => {
                        if (!info) {
                          setSelectGame(null);
                          setSelectMode(null);
                          openGameClick({ gameId: '', mode: '' });
                          return;
                        }
                        const val = info.value;
                        setSelectGame(val);
                        openGameClick({ gameId: val, mode: selectMode });
                      }}
                      label={isMobile ? 'Launching the game' : ' '}
                    />
                  </Box>
                </Tooltip>

                <ShowContainer condition={!isMobile}>
                  <Typography color="textSecondary" fontWeight={600}>
                    {t('in mode')}
                  </Typography>
                </ShowContainer>

                <Box
                  flex={1}
                  width={isMobile ? '100%' : ''}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}>
                  <CusLabelSelect
                    label={isMobile ? 'in mode' : ''}
                    value={selectMode}
                    onChange={(val) => {
                      setSelectMode(val);
                      openGameClick({ gameId: selectGame, mode: val });
                    }}
                    options={gameModeOptions}
                  />
                </Box>
              </Stack>
            }
            secondary={t('Launches the game in the selected mode')}
          />
        </ListItem>
      </ShowContainer>

      {anchors.map((el, indx) => (
        <ListItemButton key={indx} style={{ padding: 1 }} onClick={() => onAnchorClick(el.name)}>
          <ListItemText
            primary={
              <Typography color="primary" fontWeight={600}>
                {t(el.name)}
              </Typography>
            }
            secondary={t(el.description)}
          />
        </ListItemButton>
      ))}

      <Typography variant="body1" mt={2} color="textSecondary">
        {t('If the user is authorized, then the bindings are ignored, except for', {
          except: ENUM_ANCHORS.deposit
        })}
      </Typography>
      <Typography variant="body1" color="textSecondary">
        {t(
          'If the user was previously logged in to the current browser, then the ability to close the modal window will always be present'
        )}
      </Typography>
      <ShowContainer condition={exampleLink}>
        <Typography variant="inherit" mt={1} color="textSecondary">
          <b>{t('Example')}</b>: {exampleLink}
        </Typography>
      </ShowContainer>
    </List>
  );
}
